'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddToListButtonTapped from '@alltrails/amplitude/events/logTrailDetailsAddToListButtonTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import Button from '@alltrails/denali/components/Button';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import Map from '@alltrails/denali/icons/Map';
import Mobile from '@alltrails/denali/icons/Mobile';
import Share from '@alltrails/denali/icons/Share';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useSharing from '@alltrails/sharing/hooks/useSharing';
import type { TrailPage } from '@alltrails/trails/types/trailPage';
import getTrailShareObject from '@alltrails/trails/utils/getTrailShareObject';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useInstallDrawerV2PrimaryCta from '@/hooks/useInstallDrawerV2PrimaryCta';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import useSaveToListInstallPrompt from '@/hooks/useSaveToListInstallPrompt';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './MobileFooter.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });
const MiniInstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/MiniInstallInterstitial'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const reduxListsAnalyticsInfo = { detailedCardLocation: 'mobile web trail details cta bar' as CardLocation, page: PageStrings.TRAIL_PAGE };

// https://www.figma.com/design/oevB4bXh8KLLDe1Yp8rBW2/Trail-Page-Web-%5BHandoff%5D?node-id=15304-253213&m=dev
export default function MobileFooter({ trailDetails, trailPage }: Props) {
  const intl = useIntl();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const { openTrailMap } = useIsTrailMapOpen();
  const user = useUser();
  const showUnauthPrimaryCta = useInstallDrawerV2PrimaryCta();
  const getDeeplink = useDeeplink({ deeplinkData: { pageId: trailDetails.id, pageType: 'trail' } });
  const { isFavorite, handleFavoriteClick, isSaveToastOpen, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo
  );

  const { showSaveInstallPrompt, setShowSaveInstallPrompt } = useSaveToListInstallPrompt(isSaveToastOpen);

  const isBaseUser = user && !isSubscriber(user);
  const isTrailFavorite = isFavorite('trail', trailDetails.id);

  const shareObject = useMemo(() => getTrailShareObject(trailDetails), [trailDetails]);
  const trailPageType = getTrailPageType(trailDetails);

  const { isShareModalOpen, openShare, shareModalProps } = useSharing({ shareObject, trailPageType });

  const onShareClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailDetails.id.toString(),
      trail_page_action: 'share',
      trail_page_location: 'mobile cta bar',
      trail_page_type: trailPageType
    });

    openShare();
  }, [openShare, trailDetails.id, trailPageType]);

  const onOpenInAppClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailDetails.id.toString(),
      trail_page_action: 'open in app',
      trail_page_location: 'mobile cta bar',
      trail_page_type: trailPageType
    });
    window.location.assign(getDeeplink());
  }, [getDeeplink, trailDetails.id, trailPageType]);

  return (
    <div className={classNames(styles.footer, { [styles.singleCta]: showUnauthPrimaryCta })}>
      {showUnauthPrimaryCta ? (
        <Button
          icon={{ Component: Mobile }}
          onClick={onOpenInAppClick}
          text={<FormattedMessage defaultMessage="Open in app" />}
          variant="primary"
          width="full"
        />
      ) : (
        <>
          {!user ? (
            <Button icon={{ Component: Share }} onClick={onShareClick} text={<FormattedMessage defaultMessage="Share" />} variant="accent" />
          ) : isBaseUser ? (
            <Button
              icon={isTrailFavorite ? <HeartFilled /> : <Heart />}
              onClick={() => {
                logTrailDetailsActionTapped({
                  trail_id: trailDetails.id.toString(),
                  trail_page_action: 'save',
                  trail_page_location: 'mobile cta bar'
                });
                logTrailDetailsAddToListButtonTapped({ trail_id: trailDetails.id });
                handleFavoriteClick('trail', trailDetails.id);
              }}
              text={isTrailFavorite ? <FormattedMessage defaultMessage="Saved" /> : <FormattedMessage defaultMessage="Save" />}
              variant={isTrailFavorite ? 'secondary' : 'accent'}
            />
          ) : (
            <Button
              onClick={() => {
                logTrailDetailsActionTapped({
                  trail_id: trailDetails.id.toString(),
                  trail_page_action: 'download overflow menu button',
                  trail_page_location: 'mobile cta bar',
                  trail_page_type: trailPageType
                });
                setIsDownloadModalOpen(true);
              }}
              text={<FormattedMessage defaultMessage="Download" />}
              variant="accent"
            />
          )}
          <Button
            icon={!user || isBaseUser ? { Component: Map } : undefined}
            onClick={() => {
              logTrailDetailsActionTapped({
                trail_id: trailDetails.id.toString(),
                trail_page_action: 'open map',
                trail_page_location: 'mobile cta bar',
                trail_page_type: trailPageType
              });
              if (!user) {
                setIsSignUpModalOpen(true);
              } else {
                openTrailMap();
              }
            }}
            text={!user || isBaseUser ? <FormattedMessage defaultMessage="Map" /> : <FormattedMessage defaultMessage="Open map" />}
          />
        </>
      )}
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => {
            setIsDownloadModalOpen(false);
          }}
          title={<FormattedMessage defaultMessage="Download" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            openTrailMap();
          }}
          trigger="Hit the Trail Card Open Map"
          upsellTrigger="Hit the Trail Card Open Map"
        />
      )}
      {isShareModalOpen && (
        <ShareModal
          {...shareModalProps}
          onShareOptionClick={(shareOption: TrailDetailsShareOption) => {
            logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
          }}
        />
      )}
      {listModal}
      {signUpModal}
      {showSaveInstallPrompt && (
        <MiniInstallInterstitial
          buttonText={intl.formatMessage({ defaultMessage: 'Open in app' })}
          customAppPath="screen/saved"
          description={<FormattedMessage defaultMessage="Open the app to view and manage your saved adventures in one place." />}
          displayTrigger="save to list"
          featureIcon={{ Component: HeartFilled }}
          heading={<FormattedMessage defaultMessage="Access your saved trails anytime" />}
          isOpen={showSaveInstallPrompt}
          onRequestClose={() => setShowSaveInstallPrompt(false)}
          promptLocation="trail page"
          trailDetails={trailDetails}
          variant="feature-context"
        />
      )}
    </div>
  );
}
